package com.arena_web.ui.components.business

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateClubRequest
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.forms.DropdownOption
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.ui.components.widgets.MatchedText
import com.arena_web.ui.components.widgets.SuggestionMuted
import com.arena_web.utils.AddressPrediction
import com.arena_web.utils.GeoCoordinates
import com.arena_web.utils.autocompleteAddress
import com.arena_web.utils.cachedUserCoordinates
import com.arena_web.utils.getUserCoordinates
import com.arena_web.utils.resolveAddressPrediction
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.FileReader

/**
 * Club creation form shown when user has no clubs or clicks "Add Club".
 * Matches Figma design 6331-114457.
 */
@Composable
fun CreateClubContent(
    onClubCreated: () -> Unit
) {
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    var clubName by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var latitude by remember { mutableStateOf<Double?>(null) }
    var longitude by remember { mutableStateOf<Double?>(null) }
    var addressQuery by remember { mutableStateOf("") }
    var addressSuggestions by remember { mutableStateOf<List<AddressPrediction>>(emptyList()) }
    var showSuggestions by remember { mutableStateOf(false) }
    var selectedSportType by remember { mutableStateOf<String?>(null) }
    var customSportName by remember { mutableStateOf("") }
    var bannerPreviewUrl by remember { mutableStateOf<String?>(null) }
    var bannerBytes by remember { mutableStateOf<ByteArray?>(null) }
    var bannerFileName by remember { mutableStateOf("") }
    var bannerContentType by remember { mutableStateOf("image/jpeg") }
    var isCreating by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    // Seeded from the session geo cache (warmed at page load) — first
    // keystroke is already biased; the fetch below may upgrade IP to GPS.
    var userCoords by remember { mutableStateOf(cachedUserCoordinates()) }

    // Get user geolocation once for biased address search
    LaunchedEffect(Unit) {
        userCoords = getUserCoordinates()
    }

    // Debounced address search with geolocation bias
    LaunchedEffect(addressQuery) {
        if (addressQuery.length < 3 || address.isNotBlank()) {
            addressSuggestions = emptyList()
            return@LaunchedEffect
        }
        delay(300)
        addressSuggestions = autocompleteAddress(
            placesApi = container.placesApi,
            query = addressQuery,
            biasLat = userCoords?.latitude,
            biasLng = userCoords?.longitude,
        )
        showSuggestions = addressSuggestions.isNotEmpty()
    }

    // Load sports for dropdown
    val sportsList by container.sportsList.collectAsState()
    LaunchedEffect(Unit) { container.ensureSportsLoaded() }

    val sportOptions = sportsList.map { DropdownOption(value = it.name.uppercase(), label = it.name) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .sPaddingTop(40),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Outer card
        Box(
            modifier = Modifier
                .sMaxWidth(1028)
                .fillMaxWidth()
                .sBorderRadius(28)
                .styleModifier {
                    property(
                        "background",
                        "linear-gradient(176deg, rgba(60, 121, 241, 0.135) 14%, rgba(60, 121, 241, 0.045) 69%)"
                    )
                    property("backdrop-filter", "blur(${AppEffects.BACKGROUND_BLUR_MEDIUM}px)")
                }
        ) {
            // Inner card
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sBorderRadius(28)
                    .sPadding(40, 24)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(18deg, rgba(0, 59, 174, 0.03) 27%, rgba(3, 87, 230, 0.03) 68%)"
                        )
                        property("border", "1px solid rgba(41, 52, 138, 0.2)")
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    modifier = Modifier.sGap(24),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Title + subtitle + banner
                    Column(
                        modifier = Modifier.sGap(40),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Column(
                            modifier = Modifier.fillMaxWidth().sGap(12),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            SpanText(
                                "No clubs added yet",
                                modifier = Modifier
                                    .sFontSize(34)
                                    .fontWeight(FontWeight.SemiBold)
                                    .sLineHeight(40)
                                    .sLetterSpacing(0.34)
                                    .color(AppColors.Text.Secondary)
                                    .textAlign(TextAlign.Center)
                            )
                            SpanText(
                                "Add your first club to start organizing your sports facility and professional services.",
                                modifier = Modifier
                                    .sFontSize(20)
                                    .fontWeight(FontWeight.Medium)
                                    .sLineHeight(26)
                                    .sLetterSpacing(0.2)
                                    .color(AppColors.Text.Secondary)
                                    .textAlign(TextAlign.Center)
                            )
                        }

                        // Banner upload area
                        BannerUploadArea(
                            previewUrl = bannerPreviewUrl,
                            onFileSelected = { bytes, url, name, type ->
                                bannerBytes = bytes
                                bannerPreviewUrl = url
                                bannerFileName = name
                                bannerContentType = type
                            }
                        )
                    }

                    // Form fields + button
                    Column(
                        modifier = Modifier.sGap(40),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Fields
                        Column(modifier = Modifier.sGap(24)) {
                            ClubFormField(
                                label = "Name",
                                value = clubName,
                                onValueChange = { clubName = it },
                                placeholder = "Club Name"
                            )

                            ClubAddressField(
                                addressQuery = addressQuery,
                                onQueryChange = {
                                    addressQuery = it
                                    if (address.isNotBlank() && it != address) {
                                        address = ""
                                        latitude = null
                                        longitude = null
                                    }
                                },
                                suggestions = addressSuggestions,
                                showSuggestions = showSuggestions,
                                onSuggestionSelected = { prediction ->
                                    // Google predictions carry no coordinates —
                                    // one details call resolves them (and closes
                                    // the billing session).
                                    showSuggestions = false
                                    addressQuery = prediction.title
                                    scope.launch {
                                        val result =
                                            resolveAddressPrediction(container.placesApi, prediction)
                                                ?: return@launch
                                        address = listOfNotNull(
                                            result.street,
                                            result.houseNumber,
                                            result.city,
                                            result.country
                                        ).filter { it.isNotBlank() }.joinToString(", ")
                                        addressQuery = result.title
                                        latitude = result.lat
                                        longitude = result.lng
                                    }
                                },
                                onDismissSuggestions = { showSuggestions = false }
                            )

                            ClubTypeDropdown(
                                label = "Club Type",
                                options = sportOptions,
                                selectedValue = selectedSportType,
                                placeholder = "Select sport type",
                                onSelect = {
                                    selectedSportType = it.value
                                    if (it.value != "OTHER") customSportName = ""
                                }
                            )

                            if (selectedSportType == "OTHER") {
                                ClubFormField(
                                    label = "Sport Name",
                                    value = customSportName,
                                    onValueChange = { customSportName = it },
                                    placeholder = "Enter sport name"
                                )
                            }
                        }

                        // Error
                        error?.let {
                            SpanText(
                                it,
                                modifier = Modifier.sFontSize(14).color(AppColors.Error)
                            )
                        }

                        // Create Club button
                        Box(
                            modifier = Modifier
                                .sBorderRadius(22)
                                .sPadding(13, 40)
                                .backgroundColor(AppColors.Primary)
                                .cursor(if (isCreating) Cursor.NotAllowed else Cursor.Pointer)
                                .onClick {
                                    if (isCreating) return@onClick
                                    if (clubName.isBlank()) {
                                        error = "Club name is required"
                                        return@onClick
                                    }
                                    if (selectedSportType == "OTHER" && customSportName.isBlank()) {
                                        error = "Sport name is required for Other type"
                                        return@onClick
                                    }
                                    error = null
                                    isCreating = true
                                    scope.launch {
                                        val repo = container.clubAdminRepository ?: run {
                                            error = "Not authenticated"
                                            isCreating = false
                                            return@launch
                                        }

                                        // Upload banner image if selected
                                        var mainPhotoUrl: String? = null
                                        if (bannerBytes != null) {
                                            repo.uploadClubImage(
                                                bannerBytes!!, bannerFileName, bannerContentType
                                            ).onSuccess { response ->
                                                mainPhotoUrl = response.imageUrl
                                            }.onFailure { e ->
                                                error = "Image upload failed: ${e.message}"
                                                isCreating = false
                                                return@launch
                                            }
                                        }

                                        repo.createClub(
                                            CreateClubRequest(
                                                name = clubName.trim(),
                                                address = address.trim(),
                                                latitude = latitude,
                                                longitude = longitude,
                                                type = selectedSportType,
                                                customSportName = if (selectedSportType == "OTHER") customSportName.trim() else null,
                                                mainPhotoUrl = mainPhotoUrl
                                            )
                                        ).onSuccess { club ->
                                            container.selectClub(club.id)
                                            onClubCreated()
                                        }.onFailure { e ->
                                            error = e.message ?: "Failed to create club"
                                            isCreating = false
                                        }
                                    }
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                if (isCreating) "Creating..." else "Create Club",
                                modifier = Modifier
                                    .bodyLMedium()
                                    .color(AppColors.PrimaryWhite)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }
        }
    }
}

// =============================================================================
// Banner upload area
// =============================================================================

@Composable
private fun BannerUploadArea(
    previewUrl: String?,
    onFileSelected: (bytes: ByteArray, previewUrl: String, fileName: String, contentType: String) -> Unit
) {
    Column(
        modifier = Modifier.sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier
                .sWidth(310)
                .sHeight(171)
                .sBorderRadius(11)
                .overflow(Overflow.Hidden)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background-color", "#1e293b")
                }
                .onClick {
                    val input = document.createElement("input") as HTMLInputElement
                    input.type = "file"
                    input.accept = "image/*"
                    input.onchange = {
                        val file = input.files?.item(0)
                        if (file != null) {
                            val objectUrl = URL.createObjectURL(file)
                            val name = file.name
                            val type = file.type.ifBlank { "image/jpeg" }
                            val reader = FileReader()
                            reader.onload = {
                                val arrayBuffer = reader.result
                                if (arrayBuffer != null) {
                                    @Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
                                    val jsArray = js("new Uint8Array(arrayBuffer)") as org.khronos.webgl.Uint8Array
                                    val bytes = ByteArray(jsArray.length) { i -> jsArray.asDynamic()[i] as Byte }
                                    onFileSelected(bytes, objectUrl, name, type)
                                }
                            }
                            reader.readAsArrayBuffer(file)
                        }
                    }
                    input.click()
                },
            contentAlignment = Alignment.Center
        ) {
            if (previewUrl != null) {
                Img(
                    src = previewUrl,
                    alt = "Banner preview",
                    attrs = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("object-fit", "cover")
                        }
                        .toAttrs()
                )
            } else {
                Icon(
                    src = AppIcons.Camera,
                    modifier = Modifier.sSize(48).styleModifier {
                        property("opacity", "0.4")
                    }
                )
            }
        }

        SpanText(
            "Add Facility Banner",
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(22)
                .sLetterSpacing(0.09)
                .color(AppColors.Text.Muted)
                .textAlign(TextAlign.Center)
        )
    }
}

// =============================================================================
// Styled form field matching Figma design
// =============================================================================

@Composable
private fun ClubFormField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    leadingIcon: String? = null
) {
    Column(modifier = Modifier.sGap(6)) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .sLineHeight(16)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.Secondary)
                .sPaddingLeft(4)
        )

        Box(
            modifier = Modifier
                .sWidth(360)
                .sHeight(44)
                .sBorderRadius(28)
                .styleModifier {
                    property("position", "relative")
                }
        ) {
            // Gradient border
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(28))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("padding-left", s(12))
                        property("padding-right", s(8))
                        property("gap", s(12))
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (leadingIcon != null) {
                    Icon(
                        src = leadingIcon,
                        modifier = Modifier.sSize(24).styleModifier {
                            property("opacity", "0.5")
                            property("flex-shrink", "0")
                        }
                    )
                }

                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(value)
                    onInput { onValueChange(it.value) }
                    attr("placeholder", placeholder)

                    style {
                        width(100.percent)
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-family", "Urbanist, sans-serif")
                        property("font-size", s(16))
                        property("font-weight", "300")
                        property("line-height", "1.5")
                        property("letter-spacing", s(0.032))
                        property("color", AppColors.Text.Secondary.toRgbString())
                    }
                }
            }
        }
    }
}

// =============================================================================
// Styled club type dropdown matching Figma design
// =============================================================================

@Composable
private fun ClubTypeDropdown(
    label: String,
    options: List<DropdownOption>,
    selectedValue: String?,
    placeholder: String,
    onSelect: (DropdownOption) -> Unit
) {
    var isOpen by remember { mutableStateOf(false) }
    val selectedOption = options.find { it.value == selectedValue }

    Column(modifier = Modifier.sGap(6)) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .sLineHeight(16)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.Secondary)
                .sPaddingLeft(4)
        )

        Box(modifier = Modifier.position(Position.Relative)) {
            Box(
                modifier = Modifier
                    .sWidth(360)
                    .sHeight(44)
                    .sBorderRadius(28)
                    .cursor(Cursor.Pointer)
                    .onClick { isOpen = !isOpen }
                    .styleModifier {
                        property("position", "relative")
                    }
            ) {
                // Gradient border
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("border", "0.3px solid transparent")
                            property("border-radius", s(28))
                            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("-webkit-mask-composite", "xor")
                            property("mask-composite", "exclude")
                            property("pointer-events", "none")
                        }
                )
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("padding-left", s(12))
                            property("padding-right", s(8))
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                SpanText(
                    selectedOption?.label ?: placeholder,
                    modifier = Modifier
                        .flexGrow(1)
                        .sFontSize(16)
                        .fontWeight(FontWeight.Light)
                        .sLineHeight(24)
                        .sLetterSpacing(0.032)
                        .color(
                            if (selectedOption != null) AppColors.Text.Secondary
                            else AppColors.Text.Muted
                        )
                        .then(
                            if (selectedOption == null) Modifier.styleModifier {
                                property("opacity", "0.5")
                            } else Modifier
                        )
                )

                Box(
                    modifier = Modifier.sSize(32),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = AppIcons.ChevronDown,
                        modifier = Modifier.sSize(16).styleModifier {
                            if (isOpen) property("transform", "rotate(180deg)")
                        }
                    )
                }
                }
            }

            // Dropdown list
            if (isOpen) {
                Column(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .sTop(48)
                        .left(0.px)
                        .sWidth(360)
                        .sBorderRadius(16)
                        .sPadding(8)
                        .zIndex(100)
                        .overflow(Overflow.Auto)
                        .styleModifier {
                            property("border", "0.5px solid rgba(255, 255, 255, 0.3)")
                            property(
                                "background",
                                "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                    "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box"
                            )
                            property("max-height", s(200))
                        }
                ) {
                    options.forEach { option ->
                        val isSelected = option.value == selectedValue
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sBorderRadius(10)
                                .sPadding(10, 12)
                                .cursor(Cursor.Pointer)
                                .then(
                                    if (isSelected) Modifier.styleModifier {
                                        property("background", "rgba(0, 97, 255, 0.15)")
                                    } else Modifier
                                )
                                .onClick {
                                    onSelect(option)
                                    isOpen = false
                                }
                        ) {
                            SpanText(
                                option.label,
                                modifier = Modifier
                                    .bodyLMedium()
                                    .color(
                                        if (isSelected) AppColors.PrimaryWhite
                                        else AppColors.Text.Secondary
                                    )
                            )
                        }
                    }
                }
            }
        }

        // Close overlay
        if (isOpen) {
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(99)
                    .onClick { isOpen = false }
                    .styleModifier { property("background", "transparent") }
            )
        }
    }
}

// =============================================================================
// Address field with autocomplete suggestions
// =============================================================================

@Composable
private fun ClubAddressField(
    addressQuery: String,
    onQueryChange: (String) -> Unit,
    suggestions: List<AddressPrediction>,
    showSuggestions: Boolean,
    onSuggestionSelected: (AddressPrediction) -> Unit,
    onDismissSuggestions: () -> Unit
) {
    val open = showSuggestions && suggestions.isNotEmpty()
    Column(modifier = Modifier.sGap(6)) {
        SpanText(
            "Address",
            modifier = Modifier
                .sFontSize(14)
                .sLineHeight(16)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.Secondary)
                .sPaddingLeft(4)
        )

        // Click-away dismiss — under the expanded surface (99 < 100).
        if (open) {
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(99)
                    .onClick { onDismissSuggestions() }
            )
        }

        // Unified expanding surface (PersonalInfoEditModal city pattern): the
        // outer Box reserves the 44px slot; the inner Box is absolute and
        // grows downward with the suggestions inside the same rounded border,
        // floating OVER the content below.
        Box(
            modifier = Modifier
                .sWidth(360)
                .sHeight(44)
                .styleModifier { property("position", "relative") }
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMinHeight(44)
                    .sBorderRadius(28)
                    .position(Position.Absolute)
                    .zIndex(if (open) 100 else 10)
                    .styleModifier {
                        property("top", "0")
                        property("left", "0")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box"
                        )
                    }
            ) {
                // Gradient border ring (masked) — the surface's only chrome.
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("border", "0.3px solid transparent")
                            property("border-radius", s(28))
                            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("-webkit-mask-composite", "xor")
                            property("mask-composite", "exclude")
                            property("pointer-events", "none")
                        }
                )
                Column(modifier = Modifier.fillMaxWidth()) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(44)
                            .styleModifier {
                                property("padding-left", s(12))
                                property("padding-right", s(8))
                                property("gap", s(12))
                                property("box-sizing", "border-box")
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            src = AppIcons.Location,
                            modifier = Modifier.sSize(18).styleModifier {
                                property("opacity", "0.5")
                                property("flex-shrink", "0")
                            }
                        )
                        Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                            value(addressQuery)
                            onInput { onQueryChange(it.value) }
                            attr("placeholder", "Search for address or pick on map")
                            attr("autocomplete", "off")

                            style {
                                width(100.percent)
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("font-family", "Urbanist, sans-serif")
                                property("font-size", s(16))
                                property("font-weight", "300")
                                property("line-height", "1.5")
                                property("letter-spacing", s(0.032))
                                property("color", AppColors.Text.Secondary.toRgbString())
                            }
                        }
                    }

                    if (open) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(1)
                                .sMarginBottom(6)
                                .styleModifier {
                                    property("background", "rgba(255, 255, 255, 0.06)")
                                }
                        )
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier {
                                    property("padding", "0 ${s(6)} ${s(6)}")
                                    property("max-height", "240px")
                                    property("overflow-y", "auto")
                                    property("scrollbar-width", "thin")
                                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                }
                        ) {
                            suggestions.forEach { result ->
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .sPadding(8, 6)
                                        .sBorderRadius(8)
                                        .cursor(Cursor.Pointer)
                                        .onClick { onSuggestionSelected(result) }
                                ) {
                                    MatchedText(result.title, addressQuery.trim())
                                    if (result.subtitle.isNotBlank()) {
                                        SpanText(
                                            result.subtitle,
                                            modifier = Modifier
                                                .sFontSize(12)
                                                .color(SuggestionMuted)
                                                .styleModifier {
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                    property("margin-top", "2px")
                                                }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
